import { Link } from 'gatsby'
import { Picture } from 'src/components/ui/Image'
import Section from 'src/components/sections/Section'
import { InView } from 'react-intersection-observer'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { TextViewDraftJS } from '../common/TextViewDraftJS'
import { Carousel } from '../common/Carousel'
import { CallToAction } from '../common/CallToAction'
import type { MainBannerProps, Banner } from './types'
import './styles.scss'

const options = {
  autoplay: {
    delay: 5000,
    disableOnInteraction: false,
    pauseOnMouseEnter: true,
  },
  loop: true,
}

export default function MainBanner({
  banners,
  section,
  locationOnPage,
}: MainBannerProps) {
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)

  const sendPromoFeatureViewedEvent = (banner: Banner, index: number) => {
    if (!locationOnPage || !section) {
      return
    }

    const eventData = {
      ...getStandardObject(pageData),
      ...getFeatureObject({
        section,
        locationOnPage,
        name: banner?.title?.text ?? 'N/A',
        index,
      }),
    }

    dispatchAmplitudeEvent({ eventName: 'Promo Feature Viewed', eventData })
  }

  const sendAmplitudeClickEvent = (banner: Banner, index: number) => {
    if (!locationOnPage || !section) {
      return
    }

    const featureObject = getFeatureObject({
      section,
      locationOnPage,
      name: banner?.title?.text ?? '',
      index,
    })

    dispatchAmplitudeEvent({
      eventName: 'Promotional Element Selected',
      eventData: {
        ...standardObject,
        ...featureObject,
      },
    })
  }

  return (
    <Section className="campaign-main-banner">
      <Carousel options={options}>
        {banners.map((banner, index) => {
          return (
            <InView
              as="div"
              threshold={0.7}
              onChange={(inView) => {
                if (inView) {
                  sendPromoFeatureViewedEvent(banner, index)
                }
              }}
              triggerOnce
              key={index}
              onClick={() => sendAmplitudeClickEvent(banner, index)}
              onKeyDown={() => sendAmplitudeClickEvent(banner, index)}
            >
              <Link to={banner.cta.linkURL}>
                <Picture
                  sources={[
                    {
                      src: banner.images.mobile,
                      width: 360,
                      height: 440,
                      media: '(max-width: 480px)',
                    },
                    {
                      src: banner.images.tablet,
                      width: 768,
                      height: 378,
                      media: '(max-width: 1024px)',
                    },
                  ]}
                  img={{
                    src: banner.images.desktop,
                    width: 1920,
                    height: 700,
                    alt: `Imagem ${index} do carousel`,
                    loading: 'eager',
                  }}
                />
              </Link>

              <div
                style={{ color: banner.text.fontColor }}
                className="banner-info"
              >
                {banner.title.enable && banner.title.image && (
                  <img
                    className="banner-logo"
                    src={banner.title.image}
                    alt="Logo do banner"
                  />
                )}

                {banner.title.enable && banner.title.text && (
                  <h2
                    style={{ color: banner.title.fontColor }}
                    className="banner-title"
                  >
                    {banner.title.text}
                  </h2>
                )}

                {banner.text.enable && banner.text.body && (
                  <TextViewDraftJS text={banner.text.body} />
                )}

                {banner.cta.enable && (
                  <CallToAction
                    link={banner.cta.linkURL}
                    text={banner.cta.linkText}
                    fontColor={banner.cta.fontColor}
                    backgroundColor={banner.cta.backgroundColor}
                  />
                )}
              </div>
            </InView>
          )
        })}
      </Carousel>
    </Section>
  )
}
